import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, ApiError } from '../lib/apiClient';
import { CarePlanPanel } from '../components/CarePlanPanel';
import { LynkPulseFleetPanel } from '../components/LynkPulseFleetPanel';
import { sectionsForRole } from '../lib/roles';
import {
  BarChart,
  CHART_COLORS,
  ChartCard,
  ChartLegend,
  DonutChart,
  ProgressMeter,
  Sparkline,
} from '../components/charts';

/** ADM-04 — GET /admin/dashboard/metrics response shape (backend PR #380). Role-scoped:
 * SUPPORT/LEAD_SPECIALIST get `cases` only, ADMIN additionally gets `specialists`. */
interface DashboardMetrics {
  asOfUtc: string;
  cases: {
    createdToday: number;
    liveNow: number;
    completedTotal: number;
    completedToday: number;
  };
  specialists?: {
    online: number;
    offline: number;
    total: number;
  };
}

/** Auto-refresh interval for the "realtime" metrics poll — no websockets/SSE needed. */
const POLL_INTERVAL_MS = 45_000;

/**
 * How many polls the live-activity sparkline keeps. 40 × 45s ≈ 30 minutes, which is long
 * enough to see a shift change without the line compressing into noise.
 */
const TREND_SAMPLES = 40;

interface TrendPoint {
  liveNow: number;
  createdToday: number;
}

export function DashboardPage() {
  const { portalUser } = useAuth();
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /**
   * The backend exposes point-in-time counts, not history — there is no timeseries
   * endpoint to call, and inventing one here would be guessing at an API that doesn't
   * exist. So the trend is accumulated client-side from the poll this screen already runs,
   * and is labelled as being scoped to the open session so nobody reads it as all-time.
   */
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const data = await apiGet<DashboardMetrics>('/admin/dashboard/metrics');
        if (cancelled) return;
        setMetrics(data);
        setError(null);
        setTrend((prev) =>
          [...prev, { liveNow: data.cases.liveNow, createdToday: data.cases.createdToday }].slice(
            -TREND_SAMPLES,
          ),
        );
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load dashboard metrics');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    intervalRef.current = setInterval(() => void load(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  if (!portalUser) return <div className="empty-state">Loading…</div>;

  const role = portalUser.role;
  const sections = sectionsForRole(role).filter(
    (s) => s.id !== 'dashboard' && s.status === 'available',
  );

  const cases = metrics?.cases;
  const specialists = metrics?.specialists;
  const pending = loading && !metrics;

  const asOf = metrics
    ? new Date(metrics.asOfUtc).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  /** Placeholder that keeps the numeric slot from collapsing while the first poll is out. */
  const figure = (value: number | undefined) => (pending ? '…' : error && value === undefined ? '—' : (value ?? 0));

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Dashboard</h1>
          <p>
            Signed in as {portalUser.name || portalUser.email} ({role}).
            {asOf && ` Counts as of ${asOf}, refreshing every 45s.`}
          </p>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Pages v5 cards, not a bespoke strip: each tile is the kit's
          card > card-header(card-title) + card-body
          (bundle/demo/html/simply_white/index.html). */}
      <div className="stat-card-grid">
        {[
          { label: 'Cases today', value: cases?.createdToday, meta: 'Created since midnight UTC' },
          { label: 'Live now', value: cases?.liveNow, meta: 'Currently in progress' },
          { label: 'Completed today', value: cases?.completedToday, meta: 'Closed since midnight UTC' },
          { label: 'Completed all-time', value: cases?.completedTotal, meta: 'Lifetime total' },
        ].map((stat) => (
          <div className="card stat-card" key={stat.label}>
            <div className="card-header">
              <div className="card-title">{stat.label}</div>
            </div>
            <div className="card-body">
              <span className="stat-card-value">{figure(stat.value)}</span>
              <span className="stat-card-meta">{stat.meta}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Each panel fetches independently of the metrics above and of each other, so
          one endpoint failing cannot take the others down with it. */}
      <CarePlanPanel />
      <LynkPulseFleetPanel />

      <div className="chart-grid">
        <ChartCard title="Today's case flow" hint="Created → live → completed">
          {cases ? (
            <>
              <BarChart
                data={[
                  { label: 'Created', value: cases.createdToday, color: CHART_COLORS.sky },
                  { label: 'Live', value: cases.liveNow, color: CHART_COLORS.brand },
                  { label: 'Completed', value: cases.completedToday, color: CHART_COLORS.success },
                ]}
              />
              <div style={{ marginTop: '1rem' }}>
                <ProgressMeter
                  label="Completion rate today"
                  value={cases.completedToday}
                  total={cases.createdToday}
                />
              </div>
            </>
          ) : (
            <div className="chart-empty">{error ? 'Unavailable' : 'Loading…'}</div>
          )}
        </ChartCard>

        <ChartCard title="Live cases" hint="Since this page was opened">
          {/* The card leads with the current figure so it still says something on the
              first poll, when one sample isn't yet a trend the sparkline can draw. */}
          <div className="chart-headline">
            <span className="chart-headline-value">{figure(cases?.liveNow)}</span>
            <span className="chart-headline-caption">in progress right now</span>
          </div>
          <Sparkline
            values={trend.map((point) => point.liveNow)}
            emptyLabel={
              error ? 'Unavailable' : 'Trend starts with the next sample, within 45 seconds'
            }
          />
          <ChartLegend
            items={[
              {
                label: trend.length === 1 ? '1 sample so far' : `${trend.length} samples so far`,
                value: trend.length > 1 ? `${Math.min(...trend.map((t) => t.liveNow))}–${Math.max(...trend.map((t) => t.liveNow))}` : undefined,
                color: CHART_COLORS.sky,
              },
            ]}
          />
        </ChartCard>

        {/* `specialists` is only present for ADMIN — SUPPORT/LEAD_SPECIALIST get case
            counts only, so the whole card is absent rather than showing empty slots. */}
        {specialists && (
          <ChartCard title="Specialist availability" hint="Right now">
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <DonutChart
                slices={[
                  { label: 'Online', value: specialists.online, color: CHART_COLORS.success },
                  { label: 'Offline', value: specialists.offline, color: CHART_COLORS.neutral },
                ]}
                centerValue={specialists.total}
                centerCaption="Total"
              />
            </div>
            <ChartLegend
              items={[
                { label: 'Online', value: specialists.online, color: CHART_COLORS.success },
                { label: 'Offline', value: specialists.offline, color: CHART_COLORS.neutral },
              ]}
            />
          </ChartCard>
        )}
      </div>

      <h2 className="dashboard-section-title">Sections</h2>
      <div className="dashboard-links">
        {sections.map((section) => (
          <Link key={section.id} to={section.path} className="dashboard-link">
            {section.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
